import * as React from 'react'
import { Route as RouterRoute } from 'react-router'
import styled from 'styled-components'

import {
  SERVER_ERROR_CODES,
  FetchRoutesOptions,
  GroupedRoutesAirportsType,
  RoutesAirportsType,
  FetchDirectRoutesOptions,
  DirectRoutes,
  PlaceSelectList,
  PlaceType,
} from 'utils/types'
import * as api from 'utils/api'

import { Modal, ModalLimit, ModalInfo } from './Modal'
import { groupRoutes } from 'utils/groupRoutes'
import { Form } from './Form'
import RoutesScreen from './RoutesScreen'
import { ACTIVE_SEARCH_OVERLAY_CLASS } from 'components/Dropdown/InputDropdown'
import { formBreakpoint } from 'globalStyles'
import { useMedia } from 'utils/hooks'
import { convertPlacesToSelectList } from 'utils/convertPlaces'

const notifications = [
  {
    headerContent: "Sorry, we couldn't find any results",
    bodyContent: 'Please adjust your search and try again.',
    buttonContent: 'Go back to search',
  },
  {
    headerContent: 'Sorry, this link seems to be broken',
    bodyContent:
      'We cannot seem to find what you are looking for. Check the URL and try again or go back to the map.',
    buttonContent: 'Go back to search',
  },
  {
    headerContent: 'It seems like something went wrong',
    bodyContent: 'Please go back to the search screen and try again.',
    buttonContent: 'Go back to search',
  },
  {
    headerContent: 'Where do you want to go?',
    bodyContent: 'A departure and a destination must be filled in to search and see routes.',
    buttonContent: 'Go back to search',
  },
]

const Container = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
`
const SearchOverlay = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--light);
  z-index: var(--search-overlay-z-index);
  body.${ACTIVE_SEARCH_OVERLAY_CLASS} & {
    display: block;
  }
`

const MainScreen = () => {
  const [routes, setRoutes] = React.useState<GroupedRoutesAirportsType | null>(null)
  const [showLimitModal, setShowLimitModal] = React.useState(false)
  const [notificationIndex, setNotificationIndex] = React.useState(-1)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isMinimized, setIsMinimized] = React.useState(false)
  const [directRoutes, setDirectRoutes] = React.useState<DirectRoutes[] | null>(null)
  const [destination, setDestination] = React.useState<PlaceSelectList | null>(null)
  const [source, setSource] = React.useState<PlaceSelectList | null>(null)
  const isMobile = useMedia(`(max-width:${formBreakpoint})`)

  const handleCloseModal = React.useCallback(() => setShowLimitModal(false), [])

  const handleSetNotificationIndex = (index: number) => {
    if (index < -1 || index > notifications.length - 1)
      throw new Error('Index broke notification bounds')
    setNotificationIndex(index)
  }

  const notificationModalCallbacks = {
    handleCloseModal: React.useCallback(() => {
      handleSetNotificationIndex(-1)
    }, []),
  }

  const handleRoutesResponse = React.useCallback(
    (data: RoutesAirportsType) => {
      if (!data || !data.routes) {
        handleSetNotificationIndex(2)
        return
      }
      if (!data.routes.length) {
        handleSetNotificationIndex(0)
        return
      }
      if (isMobile) {
        setIsMinimized(true)
      }
      setRoutes(groupRoutes(data))
    },
    [isMobile]
  )

  const handleDestinationChange = React.useCallback((iata: string) => {
    api
      .fetchAirports(iata)
      .then((data) => {
        setDestination(convertPlacesToSelectList(data as PlaceType[])[0] ?? null)
      })
      .catch((_) => setDestination(null))
  }, [])

  const handleSourceChange = React.useCallback((iata: string) => {
    api
      .fetchAirports(iata)
      .then((data) => {
        setSource(convertPlacesToSelectList(data as PlaceType[])[0] ?? null)
      })
      .catch((_) => setSource(null))
  }, [])

  const formCallbacks = {
    handleSearchRoutes: React.useCallback(
      (options: FetchRoutesOptions) => {
        api
          .fetchRoutes(options)
          .then((data) => {
            handleRoutesResponse(data as RoutesAirportsType)
          })
          .catch((err) => {
            if (SERVER_ERROR_CODES.limitError === err) setShowLimitModal(true)
            else handleSetNotificationIndex(2)
          })
          .finally(() => setIsLoading(false))
      },
      [handleRoutesResponse]
    ),
    handleSearchDirectRoutes: React.useCallback(
      (options: FetchDirectRoutesOptions, startPoint: PlaceSelectList) => {
        const iatas = 'destination' in options ? options.destination : options.source
        if(Array.isArray(iatas)) {
          const promises = iatas.map((iata) => {
            const currentOptions = 'destination' in options ? {destination: iata} : {source: iata}
            return api.fetchDirectRoutes(currentOptions)
          })
          Promise.all(promises).then((data) => {
            const first = (startPoint as unknown) as DirectRoutes
            const directRoutes = data.reduce((sum, route) => {
              if ('airports' in route) {
                return [...sum, ...route.airports]
              }
              return sum
            }, [first])
            setDirectRoutes(directRoutes)
          })
          return
        }
        api
          .fetchDirectRoutes(options)
          .then((data) => {
            if ('airports' in data) {
              data.airports.unshift((startPoint as unknown) as DirectRoutes)
              setDirectRoutes(data.airports as DirectRoutes[])
            }
          })
          .catch((_) => {
            setDirectRoutes(null)
          })
      },
      []
    ),
    handleResetDirectRoutes: React.useCallback(() => setDirectRoutes(null), []),
    handleEmptyFields: React.useCallback(() => handleSetNotificationIndex(3), []),
    handleBrokenLink: React.useCallback(() => handleSetNotificationIndex(1), []),
    handleChangeLoading: React.useCallback(() => setIsLoading(true), []),
    handleClickMinimized: React.useCallback(() => setIsMinimized(false), []),
    handleChangeSource: React.useCallback((value: PlaceSelectList | null) => setSource(value), []),
    handleChangeDestination: React.useCallback((value: PlaceSelectList | null) => setDestination(value), []),
  }

  return (
    <Container>
      <RouterRoute
        path="/"
        render={(routeProps) => (
          <Form
            {...routeProps}
            {...formCallbacks}
            isLoading={isLoading}
            isMinimized={isMinimized}
            destination={destination}
            source={source}
          />
        )}
      />
      <Modal show={showLimitModal} handleClose={handleCloseModal}>
        <ModalLimit handleCloseModal={handleCloseModal} />
      </Modal>
      <Modal show={!!~notificationIndex} handleClose={notificationModalCallbacks.handleCloseModal}>
        <ModalInfo {...notificationModalCallbacks} {...notifications[notificationIndex]} />
      </Modal>
      <RoutesScreen
        handleInputChange={destination ? handleSourceChange : handleDestinationChange}
        routes={routes}
        directRoutes={directRoutes}
      />
      <SearchOverlay />
    </Container>
  )
}

export default MainScreen
